import { PersistenceValidationError } from '../../application/errors/PersistenceError';
import type { BackupMetadata } from '../../domain/backup/BackupMetadata';
import type { EmotionalProtocolLog } from '../../domain/emotion/EmotionalProtocolLog';
import type { JournalEntry, JournalPeriod } from '../../domain/journal/JournalEntry';
import type { PhilosophyEntry } from '../../domain/philosophy/PhilosophyEntry';
import type { AppPreferences } from '../../domain/preferences/AppPreferences';
import type { Profile } from '../../domain/profile/Profile';
import { DayMode, RoutineCategory, TaskStatus } from '../../domain/routine/enums';
import type {
  DailyRoutine,
  DailyStep,
  RoutineStepTemplate,
  RoutineTemplate,
} from '../../domain/routine/models';
import {
  asDomainId,
  type AppPreferencesId,
  type BackupMetadataId,
  type DailyRoutineId,
  type DailyStepId,
  type EmotionalProtocolLogId,
  type ExerciseLogId,
  type JournalEntryId,
  type PhilosophyEntryId,
  type ProfileId,
  type RoutineStepTemplateId,
  type RoutineTemplateId,
  type WorkoutSessionId,
} from '../../domain/shared/ids';
import type { VersionedData } from '../../domain/shared/VersionedData';
import type { ExerciseLog } from '../../domain/workout/ExerciseLog';
import type { WorkoutSession } from '../../domain/workout/WorkoutSession';
import { PERSISTENCE_RECORD_VERSION } from './databaseConfig';
import type {
  AppPreferencesRecord,
  BackupMetadataRecord,
  DailyRoutineRecord,
  DailyStepRecord,
  EmotionalProtocolLogRecord,
  ExerciseLogRecord,
  JournalEntryRecord,
  PhilosophyEntryRecord,
  ProfileRecord,
  RoutineStepTemplateRecord,
  RoutineTemplateRecord,
  VersionedDataRecord,
  WorkoutSessionRecord,
} from './records';

const taskStatuses = new Set<string>(Object.values(TaskStatus));
const dayModes = new Set<string>(Object.values(DayMode));
const routineCategories = new Set<string>(Object.values(RoutineCategory));
const journalPeriods = new Set<JournalPeriod>(['morning', 'evening']);
const logicalDatePattern = /^\d{4}-\d{2}-\d{2}$/;

function invalid(): never {
  throw new PersistenceValidationError();
}

function requireText(value: string): void {
  if (value.trim().length === 0) {
    invalid();
  }
}

function requireIsoTimestamp(value: string): void {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime()) || parsed.toISOString() !== value) {
    invalid();
  }
}

function requireLogicalDate(value: string): void {
  if (!logicalDatePattern.test(value)) {
    invalid();
  }
  const [year, month, day] = value.split('-').map(Number);
  const parsed = new Date(Date.UTC(year ?? 0, (month ?? 0) - 1, day));
  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() + 1 !== month ||
    parsed.getUTCDate() !== day
  ) {
    invalid();
  }
}

function requireFinite(value: number): void {
  if (!Number.isFinite(value)) {
    invalid();
  }
}

function requirePositiveInteger(value: number): void {
  if (!Number.isInteger(value) || value < 1) {
    invalid();
  }
}

function requireDayModes(values: readonly string[]): void {
  if (values.length === 0 || values.some((value) => !dayModes.has(value))) {
    invalid();
  }
}

function validateBase(record: {
  readonly id: string;
  readonly recordVersion: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}): void {
  requireText(record.id);
  requirePositiveInteger(record.recordVersion);
  requireIsoTimestamp(record.createdAt);
  requireIsoTimestamp(record.updatedAt);
}

function toVersionedRecord(data: VersionedData): VersionedDataRecord {
  requirePositiveInteger(data.version);
  return { version: data.version, payload: structuredClone(data.payload) };
}

function fromVersionedRecord(record: VersionedDataRecord): VersionedData {
  requirePositiveInteger(record.version);
  return { version: record.version, payload: structuredClone(record.payload) };
}

function baseRecord(entity: {
  readonly id: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}) {
  requireText(entity.id);
  requireIsoTimestamp(entity.createdAt);
  requireIsoTimestamp(entity.updatedAt);
  return {
    id: entity.id,
    recordVersion: PERSISTENCE_RECORD_VERSION,
    createdAt: entity.createdAt,
    updatedAt: entity.updatedAt,
  };
}

export const ProfileMapper = {
  toRecord(profile: Profile): ProfileRecord {
    requireText(profile.displayName);
    requireText(profile.locale);
    requireText(profile.timeZone);
    return {
      ...baseRecord(profile),
      displayName: profile.displayName,
      locale: profile.locale,
      timeZone: profile.timeZone,
    };
  },
  fromRecord(record: ProfileRecord): Profile {
    validateBase(record);
    requireText(record.displayName);
    requireText(record.locale);
    requireText(record.timeZone);
    return {
      id: asDomainId<ProfileId>(record.id),
      displayName: record.displayName,
      locale: record.locale,
      timeZone: record.timeZone,
      createdAt: record.createdAt,
      updatedAt: record.updatedAt,
    };
  },
};

export const RoutineTemplateMapper = {
  toRecord(template: RoutineTemplate): RoutineTemplateRecord {
    requireText(template.profileId);
    requireText(template.name);
    return {
      ...baseRecord(template),
      profileId: template.profileId,
      name: template.name,
      active: template.active,
      ...(template.description === undefined ? {} : { description: template.description }),
    };
  },
  fromRecord(record: RoutineTemplateRecord): RoutineTemplate {
    validateBase(record);
    requireText(record.profileId);
    requireText(record.name);
    return {
      id: asDomainId<RoutineTemplateId>(record.id),
      profileId: asDomainId<ProfileId>(record.profileId),
      name: record.name,
      active: record.active,
      createdAt: record.createdAt,
      updatedAt: record.updatedAt,
      ...(record.description === undefined ? {} : { description: record.description }),
    };
  },
};

export const RoutineStepTemplateMapper = {
  toRecord(step: RoutineStepTemplate): RoutineStepTemplateRecord {
    requireText(step.profileId);
    requireText(step.routineTemplateId);
    requireText(step.title);
    requireText(step.stepType);
    requireFinite(step.estimatedDurationMinutes);
    requireFinite(step.order);
    if (step.modes !== undefined) requireDayModes(step.modes);
    return {
      ...baseRecord(step),
      profileId: step.profileId,
      routineTemplateId: step.routineTemplateId,
      title: step.title,
      category: step.category,
      estimatedDurationMinutes: step.estimatedDurationMinutes,
      daysOfWeek: [...step.daysOfWeek],
      order: step.order,
      required: step.required,
      active: step.active,
      stepType: step.stepType,
      content: toVersionedRecord(step.content),
      ...(step.description === undefined ? {} : { description: step.description }),
      ...(step.suggestedTime === undefined ? {} : { suggestedTime: step.suggestedTime }),
      ...(step.essential === undefined ? {} : { essential: step.essential }),
      ...(step.modes === undefined ? {} : { modes: [...step.modes] }),
    };
  },
  fromRecord(record: RoutineStepTemplateRecord): RoutineStepTemplate {
    validateBase(record);
    requireText(record.profileId);
    requireText(record.routineTemplateId);
    requireText(record.title);
    requireText(record.stepType);
    requireFinite(record.estimatedDurationMinutes);
    requireFinite(record.order);
    if (!routineCategories.has(record.category)) invalid();
    if (record.modes !== undefined) requireDayModes(record.modes);
    return {
      id: asDomainId<RoutineStepTemplateId>(record.id),
      profileId: asDomainId<ProfileId>(record.profileId),
      routineTemplateId: asDomainId<RoutineTemplateId>(record.routineTemplateId),
      title: record.title,
      category: record.category as RoutineStepTemplate['category'],
      estimatedDurationMinutes: record.estimatedDurationMinutes,
      daysOfWeek: [...record.daysOfWeek],
      order: record.order,
      required: record.required,
      active: record.active,
      stepType: record.stepType,
      content: fromVersionedRecord(record.content),
      createdAt: record.createdAt,
      updatedAt: record.updatedAt,
      ...(record.description === undefined ? {} : { description: record.description }),
      ...(record.suggestedTime === undefined ? {} : { suggestedTime: record.suggestedTime }),
      ...(record.essential === undefined ? {} : { essential: record.essential }),
      ...(record.modes === undefined ? {} : { modes: record.modes as readonly DayMode[] }),
    };
  },
};

export const DailyRoutineMapper = {
  toRecord(routine: DailyRoutine): DailyRoutineRecord {
    requireText(routine.profileId);
    requireLogicalDate(routine.logicalDate);
    requireText(routine.status);
    requireFinite(routine.completionPercentage);
    if (!dayModes.has(routine.dayMode)) invalid();
    if (routine.completedAt !== undefined) requireIsoTimestamp(routine.completedAt);
    return {
      ...baseRecord(routine),
      profileId: routine.profileId,
      logicalDate: routine.logicalDate,
      dayMode: routine.dayMode,
      status: routine.status,
      completionPercentage: routine.completionPercentage,
      ...(routine.templateId === undefined ? {} : { templateId: routine.templateId }),
      ...(routine.completedAt === undefined ? {} : { completedAt: routine.completedAt }),
    };
  },
  fromRecord(record: DailyRoutineRecord): DailyRoutine {
    validateBase(record);
    requireText(record.profileId);
    requireLogicalDate(record.logicalDate);
    requireText(record.status);
    requireFinite(record.completionPercentage);
    if (!dayModes.has(record.dayMode)) invalid();
    if (record.completedAt !== undefined) requireIsoTimestamp(record.completedAt);
    return {
      id: asDomainId<DailyRoutineId>(record.id),
      profileId: asDomainId<ProfileId>(record.profileId),
      logicalDate: record.logicalDate,
      dayMode: record.dayMode as DailyRoutine['dayMode'],
      status: record.status,
      completionPercentage: record.completionPercentage,
      createdAt: record.createdAt,
      updatedAt: record.updatedAt,
      ...(record.templateId === undefined
        ? {}
        : { templateId: asDomainId<RoutineTemplateId>(record.templateId) }),
      ...(record.completedAt === undefined ? {} : { completedAt: record.completedAt }),
    };
  },
};

export const DailyStepMapper = {
  toRecord(step: DailyStep): DailyStepRecord {
    requireText(step.profileId);
    requireText(step.dailyRoutineId);
    requireText(step.title);
    requireFinite(step.order);
    requireFinite(step.estimatedDurationMinutes);
    if (!taskStatuses.has(step.status) || !routineCategories.has(step.category)) invalid();
    if (step.postponedToDate !== undefined) requireLogicalDate(step.postponedToDate);
    if (step.startedAt !== undefined) requireIsoTimestamp(step.startedAt);
    if (step.completedAt !== undefined) requireIsoTimestamp(step.completedAt);
    if (step.actualDurationMinutes !== undefined) requireFinite(step.actualDurationMinutes);
    if (step.modes !== undefined) requireDayModes(step.modes);
    return {
      ...baseRecord(step),
      profileId: step.profileId,
      dailyRoutineId: step.dailyRoutineId,
      title: step.title,
      category: step.category,
      status: step.status,
      order: step.order,
      required: step.required,
      estimatedDurationMinutes: step.estimatedDurationMinutes,
      ...(step.templateStepId === undefined ? {} : { templateStepId: step.templateStepId }),
      ...(step.description === undefined ? {} : { description: step.description }),
      ...(step.actualDurationMinutes === undefined
        ? {}
        : { actualDurationMinutes: step.actualDurationMinutes }),
      ...(step.suggestedTime === undefined ? {} : { suggestedTime: step.suggestedTime }),
      ...(step.postponedToDate === undefined ? {} : { postponedToDate: step.postponedToDate }),
      ...(step.postponedFromStepId === undefined
        ? {}
        : { postponedFromStepId: step.postponedFromStepId }),
      ...(step.postponedToStepId === undefined
        ? {}
        : { postponedToStepId: step.postponedToStepId }),
      ...(step.startedAt === undefined ? {} : { startedAt: step.startedAt }),
      ...(step.completedAt === undefined ? {} : { completedAt: step.completedAt }),
      ...(step.note === undefined ? {} : { note: step.note }),
      ...(step.data === undefined ? {} : { data: toVersionedRecord(step.data) }),
      ...(step.essential === undefined ? {} : { essential: step.essential }),
      ...(step.modes === undefined ? {} : { modes: [...step.modes] }),
    };
  },
  fromRecord(record: DailyStepRecord): DailyStep {
    validateBase(record);
    requireText(record.profileId);
    requireText(record.dailyRoutineId);
    requireText(record.title);
    requireFinite(record.order);
    requireFinite(record.estimatedDurationMinutes);
    if (!taskStatuses.has(record.status) || !routineCategories.has(record.category)) invalid();
    if (record.postponedToDate !== undefined) requireLogicalDate(record.postponedToDate);
    if (record.startedAt !== undefined) requireIsoTimestamp(record.startedAt);
    if (record.completedAt !== undefined) requireIsoTimestamp(record.completedAt);
    if (record.actualDurationMinutes !== undefined) requireFinite(record.actualDurationMinutes);
    if (record.modes !== undefined) requireDayModes(record.modes);
    return {
      id: asDomainId<DailyStepId>(record.id),
      profileId: asDomainId<ProfileId>(record.profileId),
      dailyRoutineId: asDomainId<DailyRoutineId>(record.dailyRoutineId),
      title: record.title,
      category: record.category as DailyStep['category'],
      status: record.status as DailyStep['status'],
      order: record.order,
      required: record.required,
      estimatedDurationMinutes: record.estimatedDurationMinutes,
      createdAt: record.createdAt,
      updatedAt: record.updatedAt,
      ...(record.templateStepId === undefined
        ? {}
        : { templateStepId: asDomainId<RoutineStepTemplateId>(record.templateStepId) }),
      ...(record.description === undefined ? {} : { description: record.description }),
      ...(record.actualDurationMinutes === undefined
        ? {}
        : { actualDurationMinutes: record.actualDurationMinutes }),
      ...(record.suggestedTime === undefined ? {} : { suggestedTime: record.suggestedTime }),
      ...(record.postponedToDate === undefined ? {} : { postponedToDate: record.postponedToDate }),
      ...(record.postponedFromStepId === undefined
        ? {}
        : { postponedFromStepId: asDomainId<DailyStepId>(record.postponedFromStepId) }),
      ...(record.postponedToStepId === undefined
        ? {}
        : { postponedToStepId: asDomainId<DailyStepId>(record.postponedToStepId) }),
      ...(record.startedAt === undefined ? {} : { startedAt: record.startedAt }),
      ...(record.completedAt === undefined ? {} : { completedAt: record.completedAt }),
      ...(record.note === undefined ? {} : { note: record.note }),
      ...(record.data === undefined ? {} : { data: fromVersionedRecord(record.data) }),
      ...(record.essential === undefined ? {} : { essential: record.essential }),
      ...(record.modes === undefined ? {} : { modes: record.modes as readonly DayMode[] }),
    };
  },
};

export const JournalEntryMapper = {
  toRecord(entry: JournalEntry): JournalEntryRecord {
    requireText(entry.profileId);
    requireLogicalDate(entry.logicalDate);
    return {
      ...baseRecord(entry),
      profileId: entry.profileId,
      logicalDate: entry.logicalDate,
      period: entry.period,
      data: toVersionedRecord(entry.data),
    };
  },
  fromRecord(record: JournalEntryRecord): JournalEntry {
    validateBase(record);
    requireText(record.profileId);
    requireLogicalDate(record.logicalDate);
    if (!journalPeriods.has(record.period as JournalPeriod)) invalid();
    return {
      id: asDomainId<JournalEntryId>(record.id),
      profileId: asDomainId<ProfileId>(record.profileId),
      logicalDate: record.logicalDate,
      period: record.period as JournalPeriod,
      data: fromVersionedRecord(record.data),
      createdAt: record.createdAt,
      updatedAt: record.updatedAt,
    };
  },
};

export const WorkoutSessionMapper = {
  toRecord(session: WorkoutSession): WorkoutSessionRecord {
    requireText(session.profileId);
    requireLogicalDate(session.logicalDate);
    requireText(session.status);
    return {
      ...baseRecord(session),
      profileId: session.profileId,
      logicalDate: session.logicalDate,
      status: session.status,
      data: toVersionedRecord(session.data),
    };
  },
  fromRecord(record: WorkoutSessionRecord): WorkoutSession {
    validateBase(record);
    requireText(record.profileId);
    requireLogicalDate(record.logicalDate);
    requireText(record.status);
    return {
      id: asDomainId<WorkoutSessionId>(record.id),
      profileId: asDomainId<ProfileId>(record.profileId),
      logicalDate: record.logicalDate,
      status: record.status,
      data: fromVersionedRecord(record.data),
      createdAt: record.createdAt,
      updatedAt: record.updatedAt,
    };
  },
};

export const ExerciseLogMapper = {
  toRecord(log: ExerciseLog): ExerciseLogRecord {
    requireText(log.profileId);
    requireText(log.workoutSessionId);
    requireFinite(log.order);
    return {
      ...baseRecord(log),
      profileId: log.profileId,
      workoutSessionId: log.workoutSessionId,
      order: log.order,
      data: toVersionedRecord(log.data),
    };
  },
  fromRecord(record: ExerciseLogRecord): ExerciseLog {
    validateBase(record);
    requireText(record.profileId);
    requireText(record.workoutSessionId);
    requireFinite(record.order);
    return {
      id: asDomainId<ExerciseLogId>(record.id),
      profileId: asDomainId<ProfileId>(record.profileId),
      workoutSessionId: asDomainId<WorkoutSessionId>(record.workoutSessionId),
      order: record.order,
      data: fromVersionedRecord(record.data),
      createdAt: record.createdAt,
      updatedAt: record.updatedAt,
    };
  },
};

export const PhilosophyEntryMapper = {
  toRecord(entry: PhilosophyEntry): PhilosophyEntryRecord {
    requireText(entry.title);
    requireText(entry.category);
    return {
      ...baseRecord(entry),
      title: entry.title,
      category: entry.category,
      content: toVersionedRecord(entry.content),
    };
  },
  fromRecord(record: PhilosophyEntryRecord): PhilosophyEntry {
    validateBase(record);
    requireText(record.title);
    requireText(record.category);
    return {
      id: asDomainId<PhilosophyEntryId>(record.id),
      title: record.title,
      category: record.category,
      content: fromVersionedRecord(record.content),
      createdAt: record.createdAt,
      updatedAt: record.updatedAt,
    };
  },
};

export const EmotionalProtocolLogMapper = {
  toRecord(log: EmotionalProtocolLog): EmotionalProtocolLogRecord {
    requireText(log.profileId);
    requireLogicalDate(log.logicalDate);
    return {
      ...baseRecord(log),
      profileId: log.profileId,
      logicalDate: log.logicalDate,
      data: toVersionedRecord(log.data),
    };
  },
  fromRecord(record: EmotionalProtocolLogRecord): EmotionalProtocolLog {
    validateBase(record);
    requireText(record.profileId);
    requireLogicalDate(record.logicalDate);
    return {
      id: asDomainId<EmotionalProtocolLogId>(record.id),
      profileId: asDomainId<ProfileId>(record.profileId),
      logicalDate: record.logicalDate,
      data: fromVersionedRecord(record.data),
      createdAt: record.createdAt,
      updatedAt: record.updatedAt,
    };
  },
};

export const AppPreferencesMapper = {
  toRecord(preferences: AppPreferences): AppPreferencesRecord {
    requireText(preferences.profileId);
    requireText(preferences.locale);
    return {
      ...baseRecord(preferences),
      profileId: preferences.profileId,
      locale: preferences.locale,
      data: toVersionedRecord(preferences.data),
    };
  },
  fromRecord(record: AppPreferencesRecord): AppPreferences {
    validateBase(record);
    requireText(record.profileId);
    requireText(record.locale);
    return {
      id: asDomainId<AppPreferencesId>(record.id),
      profileId: asDomainId<ProfileId>(record.profileId),
      locale: record.locale,
      data: fromVersionedRecord(record.data),
      createdAt: record.createdAt,
      updatedAt: record.updatedAt,
    };
  },
};

export const BackupMetadataMapper = {
  toRecord(metadata: BackupMetadata): BackupMetadataRecord {
    requireText(metadata.profileId);
    requireFinite(metadata.backupVersion);
    requireText(metadata.status);
    return {
      ...baseRecord(metadata),
      profileId: metadata.profileId,
      backupVersion: metadata.backupVersion,
      status: metadata.status,
    };
  },
  fromRecord(record: BackupMetadataRecord): BackupMetadata {
    validateBase(record);
    requireText(record.profileId);
    requireFinite(record.backupVersion);
    requireText(record.status);
    return {
      id: asDomainId<BackupMetadataId>(record.id),
      profileId: asDomainId<ProfileId>(record.profileId),
      backupVersion: record.backupVersion,
      status: record.status,
      createdAt: record.createdAt,
      updatedAt: record.updatedAt,
    };
  },
};
